import React, { useState, useMemo } from "react";
import { motion } from "motion/react";
import { MapPin, Search, ArrowRight, Building2, Briefcase, GraduationCap, ChevronRight, Compass, X } from "lucide-react";
import { useSeoMetadata } from "./PublicPages";
import { SeoBreadcrumbs } from "./seo/SeoBreadcrumbs";
import { 
  getAllQualifiedLocations, 
  getAllSupportedCountries,
  getAllSupportedRegions,
  getJobsLocationUrl,
  getHiringLocationUrl,
  getInternshipsLocationUrl,
  formatRegionWithCountry,
  SupportedLocation
} from "../utils/locationRegistry";

export interface LocationDirectoryPageProps {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  variant?: "jobs" | "businesses-hiring" | "internships" | "all";
  initialCountryCode?: string;
}

export const LocationDirectoryPage: React.FC<LocationDirectoryPageProps> = ({
  onNavigate,
  variant = "all",
  initialCountryCode
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string>(initialCountryCode || "ALL");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL");
  const [selectedCity, setSelectedCity] = useState<string>("ALL");

  const qualifiedLocations = useMemo(() => getAllQualifiedLocations(), []);
  const supportedCountries = useMemo(() => getAllSupportedCountries(), []);
  const supportedRegions = useMemo(() => getAllSupportedRegions(), []);

  // Filtered regions based on selected country
  const availableRegions = useMemo(() => {
    if (selectedCountry === "ALL") {
      return supportedRegions;
    }
    return supportedRegions.filter(
      r => r.countryCode === selectedCountry || r.countrySlug === selectedCountry
    );
  }, [supportedRegions, selectedCountry]);

  // Filtered cities based on selected country and region
  const availableCities = useMemo(() => {
    return qualifiedLocations
      .filter(loc => {
        const matchesCountry = selectedCountry === "ALL" || 
          loc.countryCode === selectedCountry || 
          loc.countrySlug === selectedCountry;
        
        const matchesRegion = selectedRegion === "ALL" || 
          loc.stateAbbreviation === selectedRegion || 
          loc.regionSlug === selectedRegion ||
          loc.stateName.toLowerCase().replace(/\s+/g, "-") === selectedRegion;

        return matchesCountry && matchesRegion;
      })
      .sort((a, b) => a.cityName.localeCompare(b.cityName));
  }, [qualifiedLocations, selectedCountry, selectedRegion]);

  // Cascade Handlers
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedCountry(val);
    setSelectedRegion("ALL");
    setSelectedCity("ALL");
  };

  const handleRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedRegion(val);
    setSelectedCity("ALL");
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCity(e.target.value);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCountry("ALL");
    setSelectedRegion("ALL");
    setSelectedCity("ALL");
  };

  // Main Filter Logic
  const filteredLocations = useMemo(() => {
    return qualifiedLocations.filter(loc => {
      // Country
      if (selectedCountry !== "ALL" && loc.countryCode !== selectedCountry && loc.countrySlug !== selectedCountry) {
        return false;
      }

      // Region
      if (selectedRegion !== "ALL") {
        const matchesRegion = 
          loc.stateAbbreviation === selectedRegion ||
          loc.regionSlug === selectedRegion ||
          loc.stateName.toLowerCase().replace(/\s+/g, "-") === selectedRegion;
        if (!matchesRegion) return false;
      }

      // City
      if (selectedCity !== "ALL") {
        if (loc.citySlug !== selectedCity && loc.cityName.toLowerCase() !== selectedCity.toLowerCase()) {
          return false;
        }
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matches = 
          loc.cityName.toLowerCase().includes(q) || 
          loc.stateName.toLowerCase().includes(q) || 
          loc.stateAbbreviation.toLowerCase().includes(q) ||
          loc.countryName.toLowerCase().includes(q) ||
          loc.countryCode.toLowerCase().includes(q) ||
          (loc.regionSlug && loc.regionSlug.toLowerCase().includes(q)) ||
          (loc.localHighlights?.metroName && loc.localHighlights.metroName.toLowerCase().includes(q));
        if (!matches) return false;
      }

      return true;
    });
  }, [qualifiedLocations, selectedCountry, selectedRegion, selectedCity, searchQuery]);

  // Group filtered locations by Region & Country for display
  const groupedLocations = useMemo(() => {
    const map = new Map<string, {
      regionName: string;
      regionCode: string;
      countryName: string;
      countryCode: string;
      locations: SupportedLocation[];
    }>();

    filteredLocations.forEach(loc => {
      const groupKey = `${loc.countryCode}-${loc.stateAbbreviation}`.toLowerCase();
      const existing = map.get(groupKey);
      if (existing) {
        existing.locations.push(loc);
      } else {
        map.set(groupKey, {
          regionName: loc.stateName,
          regionCode: loc.stateAbbreviation,
          countryName: loc.countryName,
          countryCode: loc.countryCode,
          locations: [loc]
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      if (a.countryName !== b.countryName) {
        return a.countryName.localeCompare(b.countryName);
      }
      return a.regionName.localeCompare(b.regionName);
    });
  }, [filteredLocations]);

  // Results summary text
  const resultsSummary = useMemo(() => {
    if (selectedCity !== "ALL") {
      const cityLoc = qualifiedLocations.find(l => l.citySlug === selectedCity);
      if (cityLoc) {
        return `Showing location for ${cityLoc.cityName}, ${cityLoc.stateName} (${cityLoc.countryName})`;
      }
    }

    if (selectedRegion !== "ALL") {
      const reg = supportedRegions.find(
        r => r.stateAbbreviation === selectedRegion || r.regionSlug === selectedRegion
      );
      if (reg) {
        return `Showing ${filteredLocations.length} published location${filteredLocations.length === 1 ? "" : "s"} in ${formatRegionWithCountry(reg.stateName, reg.countryName)}`;
      }
    }

    if (selectedCountry !== "ALL") {
      const c = supportedCountries.find(co => co.countryCode === selectedCountry);
      if (c) {
        return `Showing ${filteredLocations.length} published location${filteredLocations.length === 1 ? "" : "s"} in ${c.countryName}`;
      }
    }

    if (searchQuery.trim()) {
      return `Showing ${filteredLocations.length} published location${filteredLocations.length === 1 ? "" : "s"} matching "${searchQuery}"`;
    }

    return `Showing ${filteredLocations.length} published locations across ${supportedCountries.length} countries`;
  }, [selectedCity, selectedRegion, selectedCountry, searchQuery, filteredLocations, qualifiedLocations, supportedRegions, supportedCountries]);

  const isFiltered = searchQuery.trim() !== "" || selectedCountry !== "ALL" || selectedRegion !== "ALL" || selectedCity !== "ALL";

  let title = "Browse Local Job Search Locations | BizNearYou";
  let h1 = "Supported Job Search Locations & Regional Hubs";
  let description = "Explore supported cities and regional hubs across international markets to discover local employers, commercial activity signals, and unadvertised job opportunities.";
  let canonicalUrl = "https://getbiznearyou.com/jobs/locations";

  if (variant === "businesses-hiring") {
    title = "Browse Cities with Hiring Signal Directory | BizNearYou";
    h1 = "Cities with Active Commercial Hiring Signals";
    description = "Find cities with active commercial growth, expanding medical offices, bustling contractors, and hiring signals worth investigating.";
    canonicalUrl = "https://getbiznearyou.com/businesses-hiring/locations";
  } else if (variant === "internships") {
    title = "Browse Student Internship Locations Directory | BizNearYou";
    h1 = "Student & Early-Career Internship Locations";
    description = "Discover cities and communities with private practices, clinics, law offices, and local businesses worth pitching for student internships.";
    canonicalUrl = "https://getbiznearyou.com/internships/locations";
  }

  useSeoMetadata({
    title,
    description,
    canonicalUrl
  });

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 pb-20 pt-8"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-8">
        
        {/* Header Breadcrumb */}
        <SeoBreadcrumbs 
          items={[
            { label: "Explore", path: "/explore" },
            { 
              label: variant === "businesses-hiring" ? "Hiring Signal Locations" : variant === "internships" ? "Internship Locations" : variant === "jobs" ? "Jobs Locations" : "Locations", 
              path: variant === "businesses-hiring" ? "/businesses-hiring/locations" : variant === "internships" ? "/internships/locations" : variant === "jobs" ? "/jobs/locations" : "/locations" 
            }
          ]} 
          onNavigate={onNavigate} 
        />

        {/* Hero Section */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-xs space-y-6">
          <div className="space-y-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/50">
              <Compass className="w-3.5 h-3.5" /> Verified Local Hubs
            </span>
            <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-950 dark:text-white tracking-tight">
              {h1}
            </h1>
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 max-w-3xl leading-relaxed">
              BizNearYou connects job seekers and students directly with local employers, private practices, and commercial trade businesses across key population and regional commerce centers. Select a location below to inspect local business activity.
            </p>
          </div>

          {/* Compact Location Toolbar */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row md:items-end flex-wrap gap-3.5">
            
            {/* Search Field */}
            <div className="w-full md:w-80 md:max-w-[360px]">
              <label 
                htmlFor="location-search-input" 
                className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1"
              >
                Search Locations
              </label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 dark:text-slate-500 absolute left-3 top-2.5 pointer-events-none" />
                <input
                  id="location-search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search cities, regions, or countries"
                  className="w-full pl-9 pr-3 py-2 text-xs font-medium bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 transition shadow-2xs placeholder:text-slate-400 dark:placeholder:text-slate-500"
                />
              </div>
            </div>

            {/* Country Selector */}
            <div className="w-full md:w-44">
              <label 
                htmlFor="location-country-select" 
                className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1"
              >
                Country
              </label>
              <select
                id="location-country-select"
                value={selectedCountry}
                onChange={handleCountryChange}
                className="w-full px-3 py-2 text-xs font-medium bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 transition cursor-pointer shadow-2xs"
              >
                <option value="ALL">All countries</option>
                {supportedCountries.map(c => (
                  <option key={c.countryCode} value={c.countryCode}>
                    {c.countryName} ({c.locationCount})
                  </option>
                ))}
              </select>
            </div>

            {/* Region Selector */}
            <div className="w-full md:w-52">
              <label 
                htmlFor="location-region-select" 
                className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1"
              >
                State, province, or region
              </label>
              <select
                id="location-region-select"
                value={selectedRegion}
                onChange={handleRegionChange}
                className="w-full px-3 py-2 text-xs font-medium bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 transition cursor-pointer shadow-2xs"
              >
                <option value="ALL">All regions</option>
                {availableRegions.map(st => (
                  <option key={`${st.countryCode}-${st.stateAbbreviation}`} value={st.stateAbbreviation}>
                    {selectedCountry === "ALL" ? `${st.stateName} (${st.countryName})` : st.stateName}
                  </option>
                ))}
              </select>
            </div>

            {/* City Selector */}
            <div className="w-full md:w-48">
              <label 
                htmlFor="location-city-select" 
                className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1"
              >
                City
              </label>
              <select
                id="location-city-select"
                value={selectedCity}
                onChange={handleCityChange}
                className="w-full px-3 py-2 text-xs font-medium bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 transition cursor-pointer shadow-2xs"
              >
                <option value="ALL">All cities</option>
                {availableCities.map(c => (
                  <option key={c.citySlug} value={c.citySlug}>
                    {c.cityName}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters Button */}
            {isFiltered && (
              <div className="w-full md:w-auto pt-1 md:pt-0">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="px-3.5 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition cursor-pointer border border-slate-200 dark:border-slate-700 flex items-center justify-center gap-1.5 w-full md:w-auto"
                >
                  <X className="w-3.5 h-3.5 text-slate-500" />
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {/* Filter Summary Header & ARIA Live region */}
          <div className="flex items-center justify-between text-xs font-medium text-slate-600 dark:text-slate-400 pt-1">
            <span>{resultsSummary}</span>
            <div className="sr-only" aria-live="polite" aria-atomic="true">
              {resultsSummary}
            </div>
            {isFiltered && (
              <button 
                onClick={handleClearFilters}
                className="text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer text-xs font-semibold"
              >
                Reset all
              </button>
            )}
          </div>
        </div>

        {/* Cities Grid Grouped by Region */}
        <div className="space-y-8">
          {groupedLocations.map(group => (
            <div key={`${group.countryCode}-${group.regionCode}`} className="space-y-4">
              <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-2">
                <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  {group.regionName}, {group.countryName}
                </h2>
                <span className="text-xs bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded-full font-semibold">
                  {group.locations.length} {group.locations.length === 1 ? "Hub" : "Hubs"}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.locations.map(loc => (
                  <div 
                    key={loc.citySlug}
                    className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition flex flex-col justify-between space-y-4"
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-1.5 flex-wrap">
                            {loc.cityName}, {loc.stateAbbreviation}
                            {loc.priority === 1 && (
                              <span className="text-[10px] bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 rounded font-bold border border-amber-200 dark:border-amber-900/50">
                                Primary Metro
                              </span>
                            )}
                          </h3>
                          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                            {loc.localHighlights.metroName} &bull; {loc.countryName}
                          </p>
                        </div>
                        <span className="text-xs font-mono font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded shrink-0">
                          {loc.businessCount}+ Leads
                        </span>
                      </div>

                      <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2 leading-relaxed">
                        {loc.localHighlights.localContextText}
                      </p>

                      {loc.localHighlights.majorInstitutions && loc.localHighlights.majorInstitutions.length > 0 && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 space-x-1 pt-1">
                          <span className="font-semibold text-slate-700 dark:text-slate-300">Anchors:</span>
                          <span>{loc.localHighlights.majorInstitutions.slice(0, 2).join(", ")}</span>
                        </div>
                      )}
                    </div>

                    {/* Direct Action Links */}
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex flex-col gap-1.5 text-xs">
                      <button
                        onClick={() => onNavigate(getJobsLocationUrl(loc.citySlug))}
                        className="flex items-center justify-between text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 font-bold bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 dark:hover:bg-blue-900/60 px-3 py-1.5 rounded transition cursor-pointer"
                      >
                        <span className="flex items-center gap-1.5">
                          <Briefcase className="w-3.5 h-3.5" /> Jobs in {loc.cityName}
                        </span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>

                      <button
                        onClick={() => onNavigate(getHiringLocationUrl(loc.citySlug))}
                        className="flex items-center justify-between text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-semibold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 px-3 py-1.5 rounded transition cursor-pointer"
                      >
                        <span className="flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5" /> Businesses Hiring Signals
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                      </button>

                      <button
                        onClick={() => onNavigate(getInternshipsLocationUrl(loc.citySlug))}
                        className="flex items-center justify-between text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-semibold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 px-3 py-1.5 rounded transition cursor-pointer"
                      >
                        <span className="flex items-center gap-1.5">
                          <GraduationCap className="w-3.5 h-3.5" /> Internships in {loc.cityName}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Empty State */}
          {filteredLocations.length === 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-10 border border-slate-200 dark:border-slate-800 text-center space-y-3 shadow-xs">
              <MapPin className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto" />
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">No Locations Found</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                No verified published locations matched your search criteria. Try adjusting your country or region filter, or clear search filters.
              </p>
              <button
                type="button"
                onClick={handleClearFilters}
                className="mt-2 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
              >
                Clear Search Filters
              </button>
            </div>
          )}
        </div>

        {/* Global Scouting Search CTA Box */}
        <div className="bg-indigo-50 dark:bg-indigo-950/60 text-slate-900 dark:text-white border border-indigo-200 dark:border-indigo-900/60 rounded-2xl p-6 sm:p-8 space-y-4 text-center shadow-xs">
          <h2 className="text-xl sm:text-2xl font-black text-indigo-950 dark:text-white">
            Don't See Your City Listed Above?
          </h2>
          <p className="text-xs sm:text-sm text-indigo-800 dark:text-indigo-200 max-w-xl mx-auto leading-relaxed">
            BizNearYou's real-time local business search system allows you to search any city, ZIP code, or custom address across all supported international regions.
          </p>
          <div className="pt-2">
            <button
              onClick={() => onNavigate("/find-jobs")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-6 py-3 rounded-lg shadow-xs transition cursor-pointer inline-flex items-center gap-2"
            >
              <Search className="w-4 h-4" /> Open Full Location Search
            </button>
          </div>
        </div>

      </div>
    </motion.div>
  );
};
